// ============================================================================
// components/remote/useRemoteStats.ts — R0: ТЕЛЕМЕТРИЯ сеанса «Удалённого
// доступа» (замеры для «быстрее AnyDesk»): RTT, fps, битрейт, разрешение —
// из WebRTC getRTCStatsReport трека ScreenShare (LiveKit). Показ в шапке
// панели + периодическая запись в журнал (анти-спам 30 с) — база для бенча.
// ============================================================================

import { useEffect, useState, type MutableRefObject } from 'react';
import { Room, Track } from 'livekit-client';
import { logEvent } from '@/lib/eventLog';
import type { RemoteRole } from '@/lib/remote/remoteSession';

export interface RemoteStats {
  rttMs?: number;
  fps?: number;
  kbps?: number;
  width?: number;
  height?: number;
}

interface FlatStat {
  type?: string;
  kind?: string;
  state?: string;
  currentRoundTripTime?: number;
  framesPerSecond?: number;
  bytesReceived?: number;
  bytesSent?: number;
  frameWidth?: number;
  frameHeight?: number;
  timestamp?: number;
}

const POLL_MS = 3000;
const LOG_EVERY_MS = 30_000;

function screenShareTrack(room: Room, role: RemoteRole): Track | undefined {
  if (role === 'host') {
    return room.localParticipant.getTrackPublication(Track.Source.ScreenShare)?.track;
  }
  let found: Track | undefined;
  room.remoteParticipants.forEach((p) => {
    p.videoTrackPublications.forEach((pub) => {
      if (pub.source === Track.Source.ScreenShare && pub.videoTrack) found = pub.videoTrack;
    });
  });
  return found;
}

export function useRemoteStats(
  roomRef: MutableRefObject<Room | null>,
  sessionId: string,
  role: RemoteRole,
  active: boolean,
): RemoteStats {
  const [stats, setStats] = useState<RemoteStats>({});

  useEffect(() => {
    if (!active) return;
    let timer = 0;
    let prev: { bytes: number; ts: number } | null = null;
    let lastLog = 0;
    let stopped = false;

    const tick = async () => {
      const room = roomRef.current;
      if (!room || stopped) return;
      try {
        const track = screenShareTrack(room, role);
        if (!track) return;
        const report = await track.getRTCStatsReport();
        if (!report || stopped) return;
        const flat: FlatStat[] = [];
        report.forEach((s) => flat.push(s as unknown as FlatStat));

        let rttMs: number | undefined;
        let fps: number | undefined;
        let width: number | undefined;
        let height: number | undefined;
        let bytes = 0;
        let ts = 0;
        for (const s of flat) {
          if (s.type === 'candidate-pair' && s.state === 'succeeded' && typeof s.currentRoundTripTime === 'number') {
            rttMs = Math.round(s.currentRoundTripTime * 1000);
          }
          if ((s.type === 'inbound-rtp' || s.type === 'outbound-rtp') && s.kind === 'video') {
            if (fps === undefined) fps = s.framesPerSecond;
            if (width === undefined) width = s.frameWidth;
            if (height === undefined) height = s.frameHeight;
            bytes += s.bytesReceived || s.bytesSent || 0;
            ts = Math.max(ts, s.timestamp || 0);
          }
        }
        let kbps: number | undefined;
        if (prev && ts > prev.ts && bytes >= prev.bytes) {
          kbps = Math.round(((bytes - prev.bytes) * 8) / (ts - prev.ts)); // бит/мс = кбит/с
        }
        if (ts) prev = { bytes, ts };
        if (stopped) return;
        setStats({ rttMs, fps, kbps, width, height });

        const now = Date.now();
        if (now - lastLog >= LOG_EVERY_MS) {
          lastLog = now;
          void logEvent({
            level: 'info',
            category: 'ui',
            message: 'удалённый доступ: статистика сессии',
            entityType: 'remote',
            entityCode: 'session',
            objectId: sessionId,
            details: JSON.stringify({ rttMs, fps, kbps, width, height }),
          });
        }
      } catch {
        // статистика недоступна — не критично; ошибки не спамим
      }
    };

    timer = window.setInterval(() => void tick(), POLL_MS);
    void tick();
    return () => {
      stopped = true;
      window.clearInterval(timer);
    };
  }, [active, roomRef, sessionId, role]);

  return stats;
}
